import { colorScales } from "@octopusdeploy/design-system-tokens";
// Helper function to find color path in colorScales
export function findColorInScales(hexValue: string): string | null {
    const normalizedHex = hexValue.toUpperCase();
    const toPathValuePairs = ([colorName, colorValue]: [
        string,
        string | Record<string, string>
    ]) => {
        if (typeof colorValue === "string") {
            return [[colorName, colorValue]];
        }
        else {
            return Object.entries(colorValue).map(([shade, shadeValue]) => [`${colorName}.${shade}`, shadeValue]);
        }
    };
    const match = Object.entries(colorScales)
        .flatMap(toPathValuePairs)
        .find(([_, value]) => value.toUpperCase() === normalizedHex);
    return match ? `colorScales.${match[0]}` : null;
}
// Get the computed color value for a CSS custom property
function getComputedColorValue(cssCustomProperty: string): string {
    const documentElement = document.documentElement;
    const computedStyle = getComputedStyle(documentElement);
    const propertyName = cssCustomProperty.replace(/^var\(/, "").replace(/\)$/, "");
    return computedStyle.getPropertyValue(propertyName).trim();
}
// Check if a leaf node (color token) matches the filter
export function doesLeafNodeMatchFilter(filterValue: string, keyPath: readonly (string | number)[], cssCustomProperty: string): boolean {
    if (!filterValue.trim()) {
        return true; // No filter means show everything
    }
    const filter = filterValue.toLowerCase();
    const searchableTexts = [];
    // 1. Add the key path (property name path)
    searchableTexts.push(keyPath.slice().reverse().join(".").toLowerCase());
    // 2. Add the CSS custom property name
    searchableTexts.push(cssCustomProperty.toLowerCase());
    // 3. Add the hex color value (computed from CSS custom property)
    const computedColorValue = getComputedColorValue(cssCustomProperty);
    if (computedColorValue) {
        searchableTexts.push(computedColorValue.toLowerCase());
    }
    // 4. Add color scale path if it exists
    const colorScalePath = findColorInScales(computedColorValue || cssCustomProperty);
    if (colorScalePath) {
        searchableTexts.push(colorScalePath.toLowerCase());
    }
    // 5. Add the themeTokens path
    const themePath = `themeTokens.color.${keyPath.slice().reverse().join(".")}`;
    searchableTexts.push(themePath.toLowerCase());
    // Check if any searchable text contains the filter
    return searchableTexts.some((text) => text.includes(filter));
}
// Check if an object node has any descendants that match the filter
export function doesObjectNodeHaveMatchingDescendants(filterValue: string, data: unknown, currentKeyPath: readonly (string | number)[]): boolean {
    if (!filterValue.trim()) {
        return true; // No filter means show everything
    }
    if (typeof data === "string") {
        // This is a leaf node (color token)
        return doesLeafNodeMatchFilter(filterValue, currentKeyPath, data);
    }
    if (typeof data === "object" && data !== null) {
        // This is an object node, check all its descendants
        return Object.entries(data).some(([key, value]) => {
            const childKeyPath = [key, ...currentKeyPath];
            return doesObjectNodeHaveMatchingDescendants(filterValue, value, childKeyPath);
        });
    }
    return false;
}
